import React, { useState } from 'react';
import {
    CalendarDaysIcon,
    CheckBadgeIcon,
    ChevronDownIcon,
    ExclamationTriangleIcon,
    MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import './XFlowsDecisionBrief.css';

const CAUSAL_META = {
    confirmed: { zh: '已确认', en: 'Confirmed', tone: 'positive' },
    corroborated: { zh: '已交叉验证', en: 'Corroborated', tone: 'positive' },
    plausible: { zh: '合理线索', en: 'Plausible', tone: 'warning' },
    unresolved: { zh: '尚未解释', en: 'Unresolved', tone: 'neutral' },
};

const TMT_LABELS = {
    complete: { zh: 'TMT 周期完整', en: 'Complete TMT cycle' },
    partial: { zh: 'TMT 周期不完整', en: 'Partial TMT cycle' },
    missing: { zh: 'TMT 数据缺失', en: 'TMT unavailable' },
    holiday: { zh: 'TMT 休刊', en: 'TMT holiday' },
};

function TextList({ title, items = [] }) {
    if (!items.length) return null;
    return (
        <section className="xflows-brief__point-section">
            <h3>{title}</h3>
            <div className="xflows-brief__point-list">
                {items.map((item, index) => (
                    <div className="xflows-brief__point" key={`${item.title}-${index}`}>
                        <strong>{item.title}</strong>
                        <p>{item.detail}</p>
                        {item.evidence_label ? <small>{item.evidence_label}</small> : null}
                    </div>
                ))}
            </div>
        </section>
    );
}

function MoverTable({ movers = [], isZh }) {
    if (!movers.length) return null;
    return (
        <section className="xflows-brief__section">
            <div className="xflows-brief__section-heading">
                <h3>{isZh ? '关键波动标的' : 'Key movers'}</h3>
                <span>{movers.length}</span>
            </div>
            <div className="xflows-brief__table-wrap">
                <table className="xflows-brief__table">
                    <thead>
                        <tr>
                            <th>{isZh ? '标的 / 行情' : 'Ticker / move'}</th>
                            <th>{isZh ? '事件线索' : 'Event evidence'}</th>
                            <th>{isZh ? '因果等级' : 'Causality'}</th>
                            <th>{isZh ? '预期变化与含义' : 'Expectation / implication'}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {movers.map((mover) => {
                            const status = CAUSAL_META[mover.causal_status] || CAUSAL_META.unresolved;
                            return (
                                <tr key={mover.symbol}>
                                    <td>
                                        <strong className="xflows-brief__ticker">{mover.symbol}</strong>
                                        <span className="xflows-brief__move">{mover.price_move}</span>
                                    </td>
                                    <td>{mover.event_or_news}</td>
                                    <td>
                                        <span className={`xflows-brief__status xflows-brief__status--${status.tone}`}>
                                            {isZh ? status.zh : status.en}
                                        </span>
                                    </td>
                                    <td>
                                        <strong>{mover.expectation_change}</strong>
                                        <p>{mover.investment_implication}</p>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

function ExpectationReview({ items = [], isZh }) {
    if (!items.length) return null;
    return (
        <section className="xflows-brief__section">
            <div className="xflows-brief__section-heading">
                <h3>{isZh ? '盘前预期 → 盘后结果' : 'Premarket view → outcome'}</h3>
            </div>
            <div className="xflows-brief__review-grid">
                {items.map((item, index) => (
                    <article className="xflows-brief__review" key={`${item.topic}-${index}`}>
                        <div><strong>{item.topic}</strong><span>{item.verdict}</span></div>
                        <p><b>{isZh ? '盘前' : 'Before'}：</b>{item.premarket_view}</p>
                        <p><b>{isZh ? '结果' : 'Outcome'}：</b>{item.outcome}</p>
                    </article>
                ))}
            </div>
        </section>
    );
}

function ThemeTable({ themes = [], isZh }) {
    if (!themes.length) return null;
    return (
        <section className="xflows-brief__section">
            <div className="xflows-brief__section-heading"><h3>{isZh ? '主题与板块传导' : 'Themes and transmission'}</h3></div>
            <div className="xflows-brief__theme-list">
                {themes.map((theme, index) => (
                    <article className="xflows-brief__theme" key={`${theme.name}-${index}`}>
                        <div>
                            <strong>{theme.name}</strong>
                            <span>{theme.direction}</span>
                        </div>
                        <p>{theme.driver}</p>
                        <small>{(theme.representatives || []).join(' · ')}{theme.breadth ? ` · ${theme.breadth}` : ''}</small>
                    </article>
                ))}
            </div>
        </section>
    );
}

function WatchList({ title, icon: Icon, items = [], risk = false, isZh }) {
    if (!items.length) return null;
    return (
        <section className={`xflows-brief__watch ${risk ? 'xflows-brief__watch--risk' : ''}`}>
            <div className="xflows-brief__watch-title"><Icon aria-hidden="true" /><h3>{title}</h3></div>
            {items.map((item, index) => (
                <article key={`${item.event || item.signal}-${index}`}>
                    <strong>{item.event || item.signal}</strong>
                    <span>{item.date || (item.affected_symbols || []).join(' · ')}</span>
                    <p>{item.verification || item.trigger}</p>
                    {!risk && item.symbols?.length ? <small>{item.symbols.join(' · ')}</small> : null}
                    {risk && item.affected_symbols?.length ? <small>{item.affected_symbols.join(' · ')}</small> : null}
                </article>
            ))}
        </section>
    );
}

const XFlowsDecisionBrief = ({ brief, language = 'zh' }) => {
    const [evidenceOpen, setEvidenceOpen] = useState(false);
    const isZh = language === 'zh';
    const meta = brief?.meta || {};
    if (!brief) return null;

    const tmtLabel = TMT_LABELS[meta.tmt_status]?.[isZh ? 'zh' : 'en'] || meta.tmt_status;
    const modeLabel = meta.mode === 'tmt_led'
        ? (isZh ? 'TMT 主导' : 'TMT-led')
        : (isZh ? '多源补充' : 'Multi-source');
    const hasDisplay = Boolean(brief.display && typeof brief.display === 'object');
    const display = brief.display || {};
    const displayItems = Array.isArray(display.items)
        ? display.items.map((item) => ({
            title: item.title,
            detail: item.summary,
            evidence_label: item.evidence_label,
        }))
        : [];

    return (
        <article className="xflows-brief">
            <header className="xflows-brief__hero">
                <div className="xflows-brief__meta">
                    <span>{isZh ? '目标交易日' : 'Market date'} {meta.market_date || '—'}</span>
                    <span>{modeLabel}</span>
                    <span>{tmtLabel}</span>
                    {meta.market_data_source ? <span>{meta.market_data_source.toUpperCase()}</span> : null}
                </div>
                <h2>{display.headline || brief.headline}</h2>
                <p>{display.market_state || brief.market_state}</p>
            </header>

            <div className="xflows-brief__decision-grid">
                <TextList
                    title={isZh ? '发生了什么' : 'What happened'}
                    items={hasDisplay ? displayItems : brief.what_happened}
                />
                <TextList title={isZh ? '为什么重要' : 'Why it matters'} items={brief.why_it_matters} />
            </div>

            <MoverTable movers={brief.major_movers} isZh={isZh} />
            <ExpectationReview items={brief.expectation_review} isZh={isZh} />

            <div className="xflows-brief__lower-grid">
                <ThemeTable themes={brief.themes} isZh={isZh} />
                <aside className="xflows-brief__side">
                    <WatchList title={isZh ? '接下来验证' : 'Next checks'} icon={CalendarDaysIcon} items={brief.catalysts} isZh={isZh} />
                    <WatchList title={isZh ? '风险触发器' : 'Risk triggers'} icon={ExclamationTriangleIcon} items={brief.risks} risk isZh={isZh} />
                </aside>
            </div>

            <section className="xflows-brief__evidence">
                <button type="button" onClick={() => setEvidenceOpen((open) => !open)} aria-expanded={evidenceOpen}>
                    <span><MagnifyingGlassIcon aria-hidden="true" />{isZh ? '证据与未决问题' : 'Evidence and open questions'}</span>
                    <ChevronDownIcon aria-hidden="true" className={evidenceOpen ? 'is-open' : ''} />
                </button>
                {evidenceOpen ? (
                    <div className="xflows-brief__evidence-body">
                        <div>
                            <h4>{isZh ? '来源' : 'Sources'}</h4>
                            {(brief.sources || []).map((source, index) => (
                                <p key={`${source.name}-${index}`}>
                                    {source.url ? <a href={source.url} target="_blank" rel="noreferrer">{source.name}</a> : <strong>{source.name}</strong>}
                                    <span>{source.role}{source.as_of ? ` · ${source.as_of}` : ''}</span>
                                </p>
                            ))}
                        </div>
                        <div>
                            <h4>{isZh ? '未决问题' : 'Open questions'}</h4>
                            <ul>{(brief.open_questions || []).map((item, index) => <li key={index}>{item}</li>)}</ul>
                        </div>
                    </div>
                ) : null}
            </section>

            <footer className="xflows-brief__footer">
                <span><CheckBadgeIcon aria-hidden="true" />{isZh ? `置信度：${brief.confidence}` : `Confidence: ${brief.confidence}`}</span>
                <span>{isZh ? '仅作市场研究，不构成投资建议' : 'For market research only'}</span>
            </footer>
        </article>
    );
};

export default React.memo(XFlowsDecisionBrief);
